import React from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import { Link } from "@tiptap/extension-link";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { Underline } from "@tiptap/extension-underline";
import { TaskList } from "@tiptap/extension-task-list";
import { TaskItem } from "@tiptap/extension-task-item";
import { Image } from "@tiptap/extension-image";
import { Typography } from "@tiptap/extension-typography";
import { Markdown } from "tiptap-markdown";
import { VideoNode } from "./video-node";
import { CodeBlockHtmlEmbed } from "./code-block-html-embed";
import { ToggleBlockExtensions } from "./toggle-block";

interface ReadOnlyContentProps {
  content: Record<string, unknown> | string;
  className?: string;
}

/**
 * Detect bare video embed directives (src=https://youtube.com/embed/... or
 * src=https://loom.com/embed/...) in markdown content and wrap them in
 * fenced html-embed code blocks so the CodeBlockHtmlEmbed extension renders them.
 */
function preprocessVideoEmbeds(text: string): string {
  return text.replace(
    /^(src=https?:\/\/(?:www\.)?(?:youtube\.com\/embed\/|loom\.com\/embed\/)\S+)\n(label=.+)$/gm,
    "```html-embed\n$1\n$2\n```"
  );
}

const VIDEO_EMBED_RE = /^src=(https?:\/\/(?:www\.)?(?:youtube\.com\/embed\/|loom\.com\/embed\/)\S+)$/;

/**
 * Walk Tiptap JSON doc and convert consecutive paragraph nodes containing
 * bare video embed directives (src=..., label=...) into codeBlock nodes
 * with language="html-embed".
 */
function preprocessJsonContent(doc: Record<string, unknown>): Record<string, unknown> {
  if (!doc || !Array.isArray((doc as any).content)) return doc;
  const nodes = (doc as any).content as any[];
  const result: any[] = [];
  let i = 0;

  while (i < nodes.length) {
    const node = nodes[i];
    if (node.type === "paragraph" && node.content?.length === 1 && node.content[0].type === "text") {
      const text = node.content[0].text as string;
      const srcMatch = text.match(VIDEO_EMBED_RE);

      if (srcMatch) {
        const lines = [text];
        let j = i + 1;
        while (j < nodes.length) {
          const next = nodes[j];
          if (next.type === "paragraph" && next.content?.length === 1 && next.content[0].type === "text") {
            const nextText = next.content[0].text as string;
            if (/^(label|height)=/.test(nextText)) {
              lines.push(nextText);
              j++;
            } else {
              break;
            }
          } else {
            break;
          }
        }

        result.push({
          type: "codeBlock",
          attrs: { language: "html-embed" },
          content: [{ type: "text", text: lines.join("\n") }],
        });
        i = j;
        continue;
      }
    }
    result.push(node);
    i++;
  }

  return { ...doc, content: result };
}

const extensions = [
  StarterKit.configure({
    heading: { levels: [1, 2, 3] },
    dropcursor: false,
    gapcursor: false,
    codeBlock: false,
  }),
  CodeBlockHtmlEmbed,
  Link.configure({
    openOnClick: true,
    HTMLAttributes: {
      class: "text-primary underline underline-offset-4 cursor-pointer",
      target: "_blank",
      rel: "noopener noreferrer",
    },
  }),
  Table.configure({ resizable: false }),
  TableRow,
  TableCell,
  TableHeader,
  Underline,
  TaskList,
  TaskItem.configure({ nested: true }),
  Image.configure({
    allowBase64: true,
    HTMLAttributes: { class: "editor-image" },
  }),
  VideoNode,
  ...ToggleBlockExtensions,
  Typography,
  Markdown.configure({
    html: true,
    transformPastedText: false,
    transformCopiedText: false,
  }),
];

export const ReadOnlyContent: React.FC<ReadOnlyContentProps> = ({
  content,
  className,
}) => {
  const processedContent = React.useMemo(() => {
    if (typeof content === "string") return preprocessVideoEmbeds(content);
    return preprocessJsonContent(content);
  }, [content]);

  const editor = useEditor(
    {
      extensions,
      content: processedContent,
      editable: false,
      immediatelyRender: false,
      editorProps: {
        attributes: {
          class: [
            "article-editor prose prose-sm max-w-none",
            "text-[15px] leading-relaxed",
            "focus:outline-none",
            className || "",
          ]
            .filter(Boolean)
            .join(" "),
        },
      },
    },
    [content]
  );

  if (!editor) return null;

  return <EditorContent editor={editor} />;
};
